import { JSONContent } from '@tiptap/core';

import { TiptapContent, TiptapNodeType } from '../../tiptapTypes';
import { HIDDEN_TASK_LIST_TITLE_ATTR } from './TaskListDocument';
import { EditorState } from '@tiptap/pm/state';

type HiddenTitleAttribute = JSONContent | null | undefined;

const EMPTY_TASK_LIST_TITLE: JSONContent = { type: TiptapNodeType.taskListTitle };

/**
 * Returns the visible title node from a task list document, if present.
 */
const getTaskListTitleNode = (textContent: TiptapContent): JSONContent | undefined => {
    const firstNode = textContent?.content?.[0];

    if (firstNode?.type === TiptapNodeType.taskListTitle) return firstNode;

    return undefined;
};

/**
 * Returns whether a task list document currently renders a title node.
 */
export const hasTaskListTitle = (textContent: TiptapContent): boolean => !!getTaskListTitleNode(textContent);

/**
 * Returns whether the task list editor currently has a visible title node.
 */
export const editorStateHasTaskListTitle = (editorState: EditorState) =>
    editorState.doc.firstChild?.type.name === TiptapNodeType.taskListTitle

/**
 * Move the current title to the hidden title attribute
 */
const setHiddenTaskListTitle = (textContent: TiptapContent, visibleTitle: JSONContent): TiptapContent => {
    const { attrs, content } = textContent;

    const hiddenTitle = visibleTitle.content?.length ? visibleTitle : null;
    const nextContent = content.slice(1);

    return {
        ...textContent,
        attrs: {
            ...attrs,
            [HIDDEN_TASK_LIST_TITLE_ATTR]: hiddenTitle,
        },
        content: nextContent,
    };
};

/**
 * Move the hidden title attribute to the current title
 */
const restoreTaskListTitle = (textContent: TiptapContent): TiptapContent => {
    const { attrs, content } = textContent;

    const hiddenTitle = attrs?.[HIDDEN_TASK_LIST_TITLE_ATTR] as HiddenTitleAttribute;
    const restoredTitle = hiddenTitle?.type === TiptapNodeType.taskListTitle ? hiddenTitle : EMPTY_TASK_LIST_TITLE;

    return {
        ...textContent,
        attrs: {
            ...attrs,
            [HIDDEN_TASK_LIST_TITLE_ATTR]: null,
        },
        content: [restoredTitle, ...content],
    };
};

/**
 * Shows or hides the task list title node in persisted task list JSON.
 */
export const setTaskListTitleVisibilityInContent = (
    textContent: TiptapContent,
    shouldShowTitle: boolean,
): TiptapContent => {
    const visibleTitle = getTaskListTitleNode(textContent);

    if (shouldShowTitle && visibleTitle) return textContent;
    if (!shouldShowTitle && !visibleTitle) return textContent;

    return visibleTitle
        ? // Move the current title to the hidden title attribute
          setHiddenTaskListTitle(textContent, visibleTitle)
        : // Move the hidden title attribute to the current title
          restoreTaskListTitle(textContent);
};
